import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Chart } from 'angular-highcharts';
import { format } from 'date-fns';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { CompanyService } from 'src/app/root/company.service';

@Component({
  selector: 'lohi-loads-per-day-chart',
  templateUrl: './loads-per-day-chart.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadsPerDayChartComponent implements OnInit, OnDestroy {
  private destroy$$ = new Subject();
  public dateFormat = 'M/dd/yyyy';
  public labels: Array<string>;

  public chart: Chart;

  constructor(private companyService: CompanyService, private cd: ChangeDetectorRef) {}

  public ngOnInit() {
    this.companyService.loadsPerDay$.pipe(takeUntil(this.destroy$$)).subscribe(async (data) => {
      if (!data || !data?.entries.length) {
        return;
      }
      const labels = data.entries.map((entry) =>
        dateFormat(new Date(entry.year, entry.month - 1, entry.day, 0, 0, 0, 0).toISOString()),
      );
      const metrics = await this.companyService.companyMetrics30Day$
        .pipe(
          filter((m) => !!m),
          take(1),
        )
        .toPromise();
      this.chart = new Chart({
        chart: {
          type: 'column',
        },
        title: {
          align: 'left',
          useHTML: true,
          text: `<div class="text-lg font-bold">Loads Per Day <span class="text-blue-500">${
            metrics.daysOfHistory || 30
          }d</span></div>`,
        },
        credits: {
          enabled: false,
        },
        yAxis: [
          {
            labels: {
              enabled: false,
            },
            tickInterval: 0,
            title: { text: null },
          },
          {
            title: { text: 'Loads' },
          },
        ],
        plotOptions: {
          series: {
            stacking: 'normal',
          }
        },
        xAxis: {
          type: 'category',
          categories: labels,
          visible: true,
        },
        series: [],
        tooltip: {
          formatter() {
            return (
              `<span class="font-bold" style="color: ${this.point.color}">` + this.series.name + '</span> ' + this.y
            );
          },
        },
      });
      const loadsCompleted: Array<[string, number]> = [];
      const loadsMissed: Array<[string, number]> = [];
      for (const entry of data.entries) {
        const dateStr = dateFormat(new Date(entry.year, entry.month - 1, entry.day, 0, 0, 0, 0).toISOString());
        loadsCompleted.push([dateStr, entry.loadsCompleted]);
        loadsMissed.push([dateStr, entry.loadsMissed]);
      }
      this.chart.addSeries(
        {
          name: 'Loads Lost',
          type: 'column',
          color: '#EF6048',
          data: loadsMissed,
          yAxis: 1,
        },
        true,
        true,
      );
      this.chart.addSeries(
        {
          name: 'Loads Completed',
          type: 'column',
          color: {
            linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
            stops: [
              [0, '#070707'],
              [1, '#3e3e52'],
            ],
          },
          data: loadsCompleted,
          yAxis: 1,
        },
        true,
        true,
      );
      this.cd.markForCheck();
    });
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }
}

function dateFormat(date: string): string {
  return format(new Date(date), 'M/dd/yyyy', {
    useAdditionalDayOfYearTokens: true,
    useAdditionalWeekYearTokens: true,
  });
}
